import { ApplicationPresenter, Logger, NOOP_PROMISE_VOID, SingletonServices } from 'wdc-cube';
import { DEFAULT_PANEL_THEME, primaryRoleLabel, safePanelTheme } from '../../domain';
import { ApiClient, currentHistoryCodec, forgetHistoryKey, publicHistoryCodec, sealAddress } from '../../infra';
import { SignInKeys } from '../concrete/account/sign-in/auth.key';
import { isIncomplete, loadCompanyAccess, NO_ACCESS } from '../concrete/restricted/company-access';
import { RestrictedApi } from '../concrete/restricted/restricted.api';
import { MainKeys } from './main.key';
import { AlertScope, MainScope } from './main.scope';
import { Places } from '../route-consts';
const LOG = Logger.get('Mormaii.MainPresenter');
// @Inject
const client = ApiClient.INSTANCE;
/**
 * A aparencia da conta, lida quando a sessao nasce.
 *
 * Falhar aqui nao impede a entrada: a aparencia e preferencia, e o painel abre no
 * tema padrao.
 */
async function loadPanelTheme() {
    try {
        return safePanelTheme(await RestrictedApi.INSTANCE.fetchPanelTheme());
    }
    catch (caught) {
        LOG.warn('Could not load the panel theme', caught);
        return DEFAULT_PANEL_THEME;
    }
}
/**
 * A casca, e o unico presenter que sabe que existe sessao.
 *
 * Todo o resto assume que existe uma, porque e este que se recusa a mostrar algo
 * antes de haver - e o `hub` logo abaixo e quem aplica essa recusa.
 */
export class MainPresenter extends ApplicationPresenter {
    constructor(historyManager) {
        super(historyManager, new MainScope());
        this.bodySlot = this.setBodySlot.bind(this);
        this.dialogSlot = this.setDialogSlot.bind(this);
        this.stopServices = NOOP_PROMISE_VOID;
        /**
         * As empresas que a pessoa alcanca - dado da SESSAO, carregado quando ela nasce.
         *
         * Ver `company-access.ts`: a casca do HUB precisa disto para decidir o menu e a
         * tela de abertura, e buscava no meio da viagem. No login ja se sabe que vai ser
         * preciso, entao quem carrega e quem cria a sessao.
         */
        this.access = NO_ACCESS;
        this.setPlaces(Places);
    }
    initialize() {
        let initialized = false;
        const bootstrap = async () => {
            try {
                await this.kickStart(Places.main);
                initialized = true;
            }
            catch (error) {
                LOG.error('Initializing', error);
                this.release();
            }
        };
        bootstrap().catch(LOG.caught);
        return () => {
            if (initialized) {
                this.release();
            }
        };
    }
    release() {
        this.stopServices().catch(() => undefined);
        super.release();
    }
    async applyParameters(intent, initialization, last) {
        const keys = new MainKeys(this, intent);
        if (initialization) {
            await this.initializeState();
            /*
             * A aplicacao pode ter sido aberta num endereco profundo, e e o que o
             * navegador traz que vence - e isso que faz um link colado funcionar.
             *
             * Quem viaja para la e o `kickStart`, logo depois desta chamada: ele
             * ja compara o place do endereco com a raiz e da o flip. Aqui basta
             * SAIR - sem mandar a casca para o `hub` ou para a porta no meio do
             * caminho, que e o que o `last` abaixo faria.
             *
             * Viajar aqui tambem levava, e por isso ja estava escrito assim; mas
             * dava DOIS flips para o mesmo endereco, e o segundo reaplicava os
             * parametros de abertura sobre o que o leitor tivesse feito enquanto a
             * tela carregava - num historico de quarenta megabytes, trocar de
             * visao durante a carga voltava sozinho.
             */
            try {
                const target = this.newIntentFromString(this.historyManager.location);
                if (target.place !== intent.place) {
                    return false;
                }
            }
            catch (caught) {
                this.unexpected('Navegação a partir do histórico', caught);
            }
        }
        if (last) {
            /*
             * Ninguem mais profundo foi pedido: a casca decide para onde manda.
             *
             * Sem sessao vai para a LANDING, e nao para a porta. Quem digita o
             * endereco do site esta chegando, e merece saber o que o produto faz
             * antes de um formulario. Quem foi BARRADO numa tela interna cai no
             * `demandSignIn`, que continua indo direto ao login com o `next`.
             */
            this.bodySlot(undefined);
            await this.flip(this.session ? Places.hub : Places.landing);
            return false;
        }
        // O regime de cifra segue o destino desta viagem.
        await this.sealFor(intent.place);
        keys.parentSlot = this.bodySlot;
        keys.dialogSlot = this.dialogSlot;
        return true;
    }
    async initializeState() {
        this.stopServices = await SingletonServices.start();
        this.scope.onSignOut = this.action(this.onSignOut);
        // Sessao recusada no MEIO do uso - token expirado, ou derrubado porque a
        // mesma conta entrou em outro lugar. O painel antigo volta para a porta com
        // um aviso; sem isto o leitor fica parado numa tela que nao carrega.
        client.onSessionLost = () => {
            forgetHistoryKey();
            this.sealAddress(publicHistoryCodec());
            void this.onSessionLost();
        };
        // O token guardado pode estar morto. Perguntar agora evita a tela montar
        // com dado nenhum e cair no primeiro clique.
        await this.establishSession(await client.validateSession());
        this.scope.initialized = true;
        LOG.info('Initialized');
    }
    // ========== SESSAO ==========
    get authenticated() {
        return this.session !== undefined;
    }
    get currentSession() {
        return this.session;
    }
    /**
     * Manda o leitor para a porta, levando para onde ele ia.
     *
     * Um link para uma tela do HUB num navegador deslogado tem de abrir a porta
     * primeiro, e voltar depois para o que o link pedia - entao o intent recusado
     * viaja junto. Chamado sem ele (ao sair), nao ha a que voltar.
     */
    async demandSignIn(refused) {
        const keys = SignInKeys.of(this);
        if (refused && refused.place !== Places.signIn) {
            keys.next = refused.toString();
        }
        await keys.flip();
    }
    /**
     * Abre a sessao da pagina: aplica a sessao E carrega o que toda tela restrita vai
     * precisar dela (as empresas que a pessoa alcanca).
     *
     * Chamado nos dois momentos em que uma sessao nasce - o login e a sessao
     * restaurada no arranque (F5) -, ANTES da viagem que vem a seguir. E isso que tira
     * a consulta do meio da navegacao.
     */
    async establishSession(session) {
        this.applySession(session);
        if (session) {
            // As duas coisas que toda tela restrita usa, em paralelo: as empresas e a
            // aparencia que a pessoa escolheu.
            const [access, theme] = await Promise.all([loadCompanyAccess(session.roles), loadPanelTheme()]);
            this.access = access;
            this.scope.theme = theme;
        }
    }
    // ========== APARENCIA ==========
    get panelTheme() {
        return this.scope.theme;
    }
    /**
     * Troca a aparencia do painel - na hora, e grava na conta.
     *
     * Aplica ANTES de gravar: a troca e visual, e esperar o servidor para mudar a cor
     * faria o clique parecer travado. Se gravar falhar, volta ao tema anterior e avisa,
     * para a tela nao mostrar uma escolha que nao ficou.
     */
    async choosePanelTheme(theme) {
        const previous = this.scope.theme;
        if (!this.session || theme === previous) {
            return;
        }
        this.scope.theme = theme;
        try {
            await RestrictedApi.INSTANCE.savePanelTheme(theme);
        }
        catch (caught) {
            LOG.warn('Could not save the panel theme', caught);
            this.scope.theme = previous;
            this.alert('error', 'Aparência', 'Não foi possível salvar a aparência escolhida. Tente de novo.');
        }
    }
    /**
     * O acesso a empresas da sessao, completando a metade que falhou ao abri-la.
     *
     * No caso normal nao vai a rede: tudo veio no login. Pergunta de novo so o que nao
     * se sabe - uma falha de rede no login nao pode valer pelo resto da sessao, porque
     * o menu decide por esta resposta.
     */
    async ensureCompanyAccess() {
        const roles = this.session?.roles ?? [];
        if (isIncomplete(roles, this.access)) {
            this.access = await loadCompanyAccess(roles, this.access);
        }
        return this.access;
    }
    applySession(session) {
        if (session?.id !== this.session?.id) {
            // Outra pessoa, ou ninguem: o que a sessao anterior alcancava nao vale mais.
            this.access = NO_ACCESS;
        }
        this.session = session;
        this.scope.signedIn = session !== undefined;
        if (!session) {
            // Sem sessao o painel e Praia: a tela de entrada e de todos.
            this.scope.theme = DEFAULT_PANEL_THEME;
        }
        this.scope.userName = session?.username;
        this.scope.userEmail = session?.email;
        this.scope.roleLabel = session ? primaryRoleLabel(session.roles) : undefined;
    }
    // ========== AVISOS ==========
    unexpected(message, error) {
        super.unexpected(message, error);
        this.alert('error', 'Algo deu errado', message);
    }
    /**
     * Um aviso que PERGUNTA antes de agir.
     *
     * O painel antigo confirma a saida num `DialogAlert` ("Sair do Ambiente Admin e
     * encerrar sua sessao atual?") antes de derrubar a sessao. E o mesmo cuidado que
     * ele tem ao bloquear uma empresa - a diferenca e que ali a confirmacao e um
     * place, porque bloquear uma empresa e um lugar onde o leitor esta; sair nao e.
     */
    confirm(title, message, confirmText, onConfirm, options) {
        const scope = new AlertScope();
        scope.severity = 'warning';
        scope.title = title;
        scope.message = message;
        scope.confirmText = confirmText;
        scope.cancelText = options?.cancelText ?? 'Cancelar';
        scope.icon = options?.icon;
        scope.onClose = this.action(this.onCloseAlert.bind(this, undefined));
        scope.onConfirm = this.action(async () => {
            this.scope.alert = undefined;
            await onConfirm();
        });
        scope.update = this.update;
        this.scope.alert = scope;
    }
    /**
     * Um aviso com UM botao, cujo rotulo quem avisa escolhe.
     *
     * O painel antigo diz "Continuar" nos avisos de "salvo com sucesso", e nao
     * "Ok" - e poe um icone ao lado do titulo. Sem `cancelText` o dialogo nao
     * ganha o botao de recusa: nao ha o que recusar num aviso.
     */
    notify(title, message, confirmText, icon) {
        const scope = new AlertScope();
        scope.severity = 'info';
        scope.title = title;
        scope.message = message;
        scope.confirmText = confirmText;
        scope.icon = icon;
        scope.onClose = this.action(this.onCloseAlert.bind(this, undefined));
        scope.onConfirm = this.action(this.onCloseAlert.bind(this, undefined));
        scope.update = this.update;
        this.scope.alert = scope;
    }
    alert(severity, title, message, onClose) {
        const scope = new AlertScope();
        scope.severity = severity;
        scope.title = title;
        scope.message = message;
        scope.onClose = this.action(this.onCloseAlert.bind(this, onClose));
        scope.update = this.update;
        this.scope.alert = scope;
    }
    // ========== SLOTS ==========
    setBodySlot(scope) {
        this.scope.body = scope ?? undefined;
    }
    setDialogSlot(scope) {
        if (this.scope.dialog !== scope) {
            this.scope.dialog = scope;
            if (this.scope.dialog && !this.scope.dialog.onClose) {
                LOG.error(`Missing onClose action on scope ${this.scope.dialog.constructor.name}`);
            }
        }
    }
    // ========== ACOES ==========
    async onCloseAlert(onClose) {
        this.scope.alert = undefined;
        if (onClose) {
            await onClose();
        }
    }
    /**
     * A sessao caiu enquanto a pessoa usava o painel.
     *
     * Idempotente de proposito: seis requisicoes falhando juntas - o painel pede
     * seis series de uma vez - chamam isto seis vezes, e so a primeira tem o que
     * fazer. `applySession(undefined)` derruba `authenticated` na hora, antes de
     * qualquer await, entao as outras cinco saem pela porta de cima.
     *
     * Quem ja esta deslogado nao e levado a lugar nenhum: um 403 numa tela PUBLICA,
     * como o historico compartilhado, nao pode arrastar o visitante para a porta.
     */
    async onSessionLost() {
        if (!this.authenticated) {
            return;
        }
        client.clearSession();
        this.applySession(undefined);
        await this.demandSignIn();
        this.alert('error', 'Aviso', 'Sua sessão expirou. Efetue o login novamente.');
    }
    /**
     * Pergunta antes de sair, como o painel antigo pergunta.
     *
     * Publico porque quem oferece o botao e o menu do HUB, e o menu vive no
     * `RestrictedPresenter` - a sessao, nao.
     */
    async confirmSignOut() {
        // Titulo, mensagem, rotulos e icone sao os do painel antigo, letra por letra.
        this.confirm('Sair', 'Sair do Ambiente Admin e encerrar sua sessão atual?', 'Sim, Sair', async () => {
            await this.onSignOut();
        }, { cancelText: 'Cancelar', icon: 'door-open' });
    }
    /**
     * Sair de fato.
     *
     * Sem `refused`: ao sair nao ha destino guardado para voltar depois.
     */
    async onSignOut() {
        client.clearSession();
        this.applySession(undefined);
        forgetHistoryKey();
        this.sealAddress(publicHistoryCodec());
        await this.demandSignIn();
    }
    /**
     * Liga ou desliga a cifra do endereco.
     *
     * O `updateHistory()` e o que republica o endereco corrente sob a decisao nova -
     * sem ele, a barra continuaria mostrando o que a anterior escreveu ate a proxima
     * navegacao.
     */
    sealAddress(codec) {
        if (this.historyManager.codec === codec) {
            return;
        }
        this.historyManager.codec = codec;
        this.updateHistory();
    }
    /**
     * O endereco selado, para quem precisa ESCREVER um endereco fora da barra.
     *
     * Quem publica endereco normalmente e o `HistoryManager`, e ele ja cifra. O
     * `href` de um link nao passa por ele: e uma string montada na mao, e sem selo
     * ela chega em texto claro - que o `SealedHistoryManager` recusa. Entao quem
     * monta um `href` pede o selo aqui.
     */
    sealedAddress(plain) {
        return sealAddress(plain, this.historyManager.codec);
    }
    /**
     * Escolhe o regime pelo LUGAR, e nao pela sessao.
     *
     * Dentro do `hub` sela com a chave da pessoa; fora dele, com a chave publica. A
     * diferenca importa num caso concreto: um profissional logado abre um historico
     * compartilhado e copia o endereco para um colega. Selado com a chave dele, o
     * colega nao abriria - e aquele link existe justamente para ser passado adiante.
     *
     * Ler nao tem esse problema: o codec do usuario tem a chave publica em
     * `previous`, entao ele decifra os dois.
     */
    async sealFor(place) {
        // `x.contains(y)` e "y esta no caminho de x": o argumento e o ANCESTRAL.
        const restrito = place.contains(Places.hub);
        // No restrito PREFERE a chave da pessoa, mas cai na publica quando nao ha -
        // servidor sem o segredo mestre, por exemplo. Cair em `undefined` deixaria o
        // endereco em claro justamente onde ele carrega CPF.
        this.sealAddress(restrito ? await currentHistoryCodec() : publicHistoryCodec());
    }
}
//# sourceMappingURL=main.presenter.js.map